/**
 * Utility functions for handling customer JWT authentication tokens
 */
// Storage keys for the JWT tokens
export const CUSTOMER_JWT_KEY = 'customer_jwt_token';
export const CUSTOMER_JWT_EXPIRY_KEY = 'customer_jwt_expiry';

// One week in milliseconds
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Store the customer JWT token in local storage with a 1-week expiration
 * @param token - The JWT token to store
 */
export const storeCustomerJWT = (token: string): void => {
  if (!token) {
    return;
  }

  // Set the token
  localStorage.setItem(CUSTOMER_JWT_KEY, token);
  // Set the expiration timestamp (current time + 1 week)
  const expiryTime = Date.now() + ONE_WEEK_MS;
  localStorage.setItem(CUSTOMER_JWT_EXPIRY_KEY, expiryTime.toString());
};

/**
 * Check if the stored JWT token is expired
 * @returns Boolean indicating if the token is expired or not
 */
export const isJWTExpired = (): boolean => {
  const expiryTimeStr = localStorage.getItem(CUSTOMER_JWT_EXPIRY_KEY);

  if (!expiryTimeStr) {
    return true;
  }

  const expiryTime = parseInt(expiryTimeStr, 10);
  return Date.now() > expiryTime;
};

/**
 * Remove the customer JWT token from local storage
 */
export const removeCustomerJWT = (): void => {
  localStorage.removeItem(CUSTOMER_JWT_KEY);
  localStorage.removeItem(CUSTOMER_JWT_EXPIRY_KEY);
};

/**
 * Retrieve the customer JWT token from local storage
 * @returns The JWT token if valid and not expired, or null otherwise
 */
export const getCustomerJWT = (): string | null => {
  // First check if the token has expired
  if (isJWTExpired()) {
    // If expired, clean up and return null
    removeCustomerJWT();
    return null;
  }

  return localStorage.getItem(CUSTOMER_JWT_KEY);
};

/**
 * Check if the JWT token is valid and for a logged-in user
 * @param jwt - The JWT token to check
 * @returns Boolean indicating if the token is valid
 */
export const isValidCustomerJWT = (jwt: string): boolean => {
  if (!jwt) {
    return false;
  }

  // If the JWT contains "errors" or is too short, it's likely invalid
  if (jwt.includes('errors') || jwt.length < 10) return false;

  try {
    // Basic structure check - JWTs have 3 parts separated by dots
    return jwt.split('.').length === 3;
  } catch (error) {
    // Removed console.error to fix lint errors
    return false;
  }
};

/**
 * Fetch the current customer JWT from BigCommerce
 * @param apiAccountClientId - The API client ID
 * @returns A Promise resolving to the JWT token
 */
export const fetchCustomerJWT = async (apiAccountClientId: string): Promise<string> => {
  const resource = `/customer/current.jwt?app_client_id=${apiAccountClientId}`;

  // First check if we already have a valid and non-expired token in localStorage
  const existingToken = getCustomerJWT();

  if (existingToken && isValidCustomerJWT(existingToken)) {
    return existingToken;
  }

  // Make the fetch request directly without the unnecessary try/catch
  const response = await fetch(resource);

  if (response.status === 200) {
    const jwt = await response.text();

    // Only store the JWT if it's valid and the user is logged in
    if (isValidCustomerJWT(jwt)) {
      storeCustomerJWT(jwt);
    }

    return jwt;
  }
  if (response.status === 404) {
    // If endpoint not found, this could be in the development environment
    // or because the user isn't logged in yet
    return '';
  }
  throw new Error(`Failed to fetch JWT. Status: ${response.status}`);
};

/**
 * Add JWT authentication to fetch requests
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @returns Promise with fetch response
 */
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const jwt = getCustomerJWT();

  // Note: We might want to notify the user or implement fallback auth if no JWT is found
  const authOptions: RequestInit = {
    ...options,
    headers: {
      ...options.headers,
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
  };

  return fetch(url, authOptions);
};
